import React, { useState, useEffect, useContext } from 'react';
import { Form } from '@availity/form';
import { Storage } from 'aws-amplify';
import * as yup from 'yup';
import { SelectField } from '@availity/select';
import moment from 'moment';
import {
  Button,
  DropdownToggle,
  DropdownMenu,
  Dropdown,
  Row,
  Col,
} from 'reactstrap';
import { Vars } from '@/utils';
import { FaCalendarMinus } from 'react-icons/fa';
import { dateRange } from '@availity/yup';
import { DateRangeField } from '@availity/date';
import {
  leadAggregationQuery,
  publisherManyQuery,
  reportManyQuery,
} from '@/graphql/queries';
import { useQuery } from '@apollo/react-hooks';
import { useAdmin } from '@/hooks';
import LeadsShowExportModal from '@/modals/LeadsShowExportModal';
import omit from 'lodash.omit';
import { reportOptions } from './reportConstants';

interface ReportControlsProps {
  agentID: string;
}
const ReportControls: React.SFC<ReportControlsProps> = ({ agentID }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { publisherID, vendorID } = useAdmin();
  const [showExportModal, setShowExportModal] = useState(false);
  const { data = {}, loading } = useQuery(reportManyQuery, {
    skip: !publisherID && !vendorID,
    variables: {
      folderID: publisherID || vendorID,
      reportFolder: publisherID ? 'publisher' : 'vendor',
    },
  });

  const toggleDropdown = () => setDropdownOpen((o) => !o);

  const { reportMany = [] } = data;

  if (loading) return null;

  const noGeneratingReports = reportMany.every(
    ({ status }: any) => status !== 'GENERATING'
  );

  return (
    <div style={{ marginBottom: 10 }}>
      <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
        <DropdownToggle split caret>
          Report Generator{' '}
        </DropdownToggle>
        <DropdownMenu className="p-4" style={{ width: 600 }}>
          <Row xs={12}>
            <Form
              initialValues={{
                reportType: '',
                publisherID: '',
                vendorName: '',
                dateRange: {
                  startDate: moment({ hour: 0 }).format(),
                  endDate: moment({ hour: 0 }).format(),
                },
              }}
              validationSchema={yup.object().shape({
                reportType: yup
                  .string()
                  .required('This field is required.')
                  .isRequired(),
                dateRange: dateRange({}),
              })}
              onSubmit={(values) => {
                const { reportType, vendorName, dateRange } = values;
                const filePath = `leads/exports/searchPayload-${moment().format()}.json`;

                const { startDate = '', endDate = '' } = dateRange as any;
                Storage.vault.put(
                  filePath,
                  JSON.stringify({
                    reportFolder: publisherID ? 'publisher' : 'vendor',
                    type: reportType,
                    folderID: publisherID || vendorID,
                    filter: {
                      vendorID,
                      publisherID:
                        publisherID || values.publisherID || undefined,
                      vendorName,
                      brokerID: '',
                      startDate,
                      endDate,
                    },
                  }),
                  {
                    level: 'private',
                    contentType: 'application/json',
                    bucket: `report-filters-${Vars.env}`,
                    region: 'us-east-1',
                  }
                );
                setShowExportModal(true);
                toggleDropdown();
              }}
            >
              {({ values }) => {
                const { data: leadAggData } = useQuery(leadAggregationQuery, {
                  variables: {
                    fields: ['publisherID.keyword'],
                    filter: omit(
                      {
                        created: {
                          gte: values.dateRange.startDate,
                          lte: values.dateRange.endDate,
                        },
                      },
                      ['publisherID']
                    ),
                  },
                });

                const { data: publisherData } = useQuery(publisherManyQuery, {
                  variables: {
                    publisherIDs:
                      leadAggData?.leadAggregation
                        .find((f: any) => f.field === 'publisherID.keyword')
                        .counts.map(({ key }: any) => key) || [],
                  },
                });

                const { publisherMany: pubMany = [] } = publisherData || {};
                return (
                  <Row style={{ marginLeft: 10, marginRight: 10 }}>
                    <Col xs={12}>
                      <DateRangeField
                        className="leads-date-range"
                        name="dateRange"
                        id="dateRange"
                        label="Date Range"
                        customArrowIcon="-"
                        calendarIcon={
                          <div>
                            <FaCalendarMinus />
                          </div>
                        }
                        datepickerProps={{
                          renderMonthElement: undefined,
                        }}
                        ranges
                      />
                    </Col>
                    <Col xs={6}>
                      <SelectField
                        name="reportType"
                        label="Report Type"
                        className="left-filter-select"
                        valueKey="reportType"
                        placeholder="Select report type..."
                        options={reportOptions.filter(
                          ({ reportType }) =>
                            !(
                              vendorID !== 'advercates' &&
                              reportType === 'advercates'
                            )
                        )}
                        getOptionLabel={(opt: any) => {
                          return opt.label;
                        }}
                      />
                    </Col>
                    {vendorID && (
                      <Col xs={6}>
                        <SelectField
                          name="publisherID"
                          label="Publisher"
                          isClearable
                          labelKey="publisherID"
                          valueKey="publisherID"
                          options={pubMany}
                          getOptionLabel={(opt: any) => {
                            return opt.publisherName;
                          }}
                        />
                      </Col>
                    )}
                    <Col xs={12}>
                      <Button
                        type="submit"
                        size="md"
                        color="primary"
                        disabled={!noGeneratingReports}
                      >
                        Generate
                      </Button>
                      {!noGeneratingReports && (
                        <span className="text-danger ml-2">
                          You can only generate one report at a time.
                        </span>
                      )}
                    </Col>
                  </Row>
                );
              }}
            </Form>
          </Row>
        </DropdownMenu>
      </Dropdown>
      <LeadsShowExportModal
        toggle={() => setShowExportModal((o) => !o)}
        isOpen={showExportModal}
      />
    </div>
  );
};

export default ReportControls;
